import { FC, useMemo, useState } from 'react';
import { FaIcon } from 'fe-learner-common';
import { useIntl } from 'react-intl';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { EmptyStateWrapper } from '@fe-learner-common/components/EmptyState';
import { TPerson } from '@api/io/session.io';
import { Person } from '@components/Person';
import { ContextPagination as Pagination } from '@components/ContextPagination';
import { usePageParam } from '@hooks/params/usePageParam';
import { TabPage } from '@routes/session-details/components/TabPage';
import { PaginationPageSize } from '@fe-core-ui/src/components/Pagination/PageSizeSelect';
type InstructorsTab = {
  persons?: TPerson[] | null;
};

export const InstructorsTab: FC<InstructorsTab> = ({ persons }) => {
  const intl = useIntl();
  const [pageSize, setPageSize] = useState<PaginationPageSize>(8);
  const { page, setPage } = usePageParam();
  const pageIndex = page - 1;

  const paginatedPersons = useMemo(() => {
    if (!persons || !persons?.length) {
      return [];
    }

    const calculatedPageSize = pageSize === 'all' ? persons.length : pageSize;
    return persons?.slice(
      pageIndex * calculatedPageSize,
      (pageIndex + 1) * calculatedPageSize,
    );
  }, [persons, pageIndex, pageSize]);

  return (
    <TabPage
      title={intl.formatMessage({ id: 'catalog.tab.instructors.title' })}
    >
      <>
        <EmptyStateWrapper
          icon={<FaIcon icon={faUser} className="text-neutral-550" />}
          label={intl.formatMessage({
            id: 'courses.tab.participants.empty.label',
          })}
          description={intl.formatMessage({
            id: 'courses.tab.participants.empty.description',
          })}
          condition={!!persons?.length}
        >
          <div className="grid grid-cols-1 items-center  justify-center gap-4 md:grid-cols-2 xl:grid-cols-3">
            {paginatedPersons?.map((person) => (
              <Person key={`participant-list-${person.id} `} person={person} />
            ))}
          </div>
        </EmptyStateWrapper>
        <Pagination
          type="link"
          actionContext="Instructors"
          className="px-4 py-3"
          page={page}
          totalNoItems={persons?.length || 0}
          pageSizeOptions={{
            pageSize,
            onPageSizeChange: (pageSize) => {
              setPageSize(pageSize);
            },
          }}
          setPageParams={(page) => {
            setPage(page.toString());
          }}
          texts={{
            ariaLabel: intl.formatMessage({
              id: 'courses.tab.instructors.pagination.ariaLabel',
            }),
          }}
        />
      </>
    </TabPage>
  );
};
